/**
 * Third-Party Widget Lazy Loader
 * Defers loading of heavy third-party widgets until needed
 * Improves initial page load performance
 */

(function() {
  'use strict';

  /**
   * Lazy load Google Merchant Widget
   */
  function lazyLoadMerchantWidget() {
    // Check if widget container exists
    const container = document.getElementById('merchantwidgetiframe');
    if (!container) return;

    // Don't load if already loaded
    if (container.hasAttribute('data-loaded')) return;

    // Create intersection observer
    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          // Load merchant widget script
          const script = document.createElement('script');
          script.src = 'https://www.gstatic.com/shopping/merchant/merchantwidget.js';
          script.async = true;
          script.onload = function() {
            console.log('Google Merchant Widget loaded');
          };
          document.head.appendChild(script);

          container.setAttribute('data-loaded', 'true');
          observer.disconnect();
        }
      });
    }, {
      rootMargin: '200px' // Start loading 200px before visible
    });

    observer.observe(container);
  }

  /**
   * Lazy load chat widgets (if present)
   */
  function lazyLoadChatWidgets() {
    // Delay chat widget loading by 5 seconds or until user interaction
    let loaded = false;

    function loadChatWidget() {
      if (loaded) return;
      loaded = true;

      // Check if Avada LiveChat is enabled
      if (window.AVADA_LIVECHAT && window.AVADA_LIVECHAT.enabled) {
        console.log('Loading chat widget...');
        // Chat widget will be loaded by Avada scripts
      }
    }

    // Load after 5 seconds of idle time
    setTimeout(loadChatWidget, 5000);

    // Or load on first user interaction
    const interactionEvents = ['mousemove', 'scroll', 'touchstart', 'click'];
    interactionEvents.forEach(function(eventType) {
      document.addEventListener(eventType, function() {
        loadChatWidget();
      }, { once: true, passive: true });
    });
  }

  /**
   * Defer Avada scripts to after page load
   */
  function optimizeAvadaLoading() {
    // Avada scripts are already loaded via Shopify app
    // But we can optimize when they execute

    // Check if on product page
    const isProductPage = document.body.classList.contains('template-product');

    if (isProductPage && window.AVADA_CDT) {
      // Defer non-critical Avada features
      if (window.requestIdleCallback) {
        window.requestIdleCallback(function() {
          // Initialize non-critical Avada features
          if (window.AVADA_SP_V2) {
            console.log('Initializing Avada features in idle time');
          }
        }, { timeout: 3000 });
      }
    }
  }

  /**
   * Lazy load slick carousel CSS and JS only when needed
   */
  function lazyLoadSlick() {
    // Check if any slick elements exist
    const slickElements = document.querySelectorAll('.featured-collection .grid, .promo-grid .flex-grid, .homepagegrid');

    if (slickElements.length === 0) return;

    // Load slick CSS if not already loaded
    const slickCSS = document.querySelector('link[href*="slick.css"]');
    if (!slickCSS || slickCSS.media === 'print') {
      // CSS is set to load on print, change to all
      if (slickCSS) {
        slickCSS.media = 'all';
      }
    }

    // Check if slick is already loaded
    if (window.jQuery && window.jQuery.fn.slick) {
      console.log('Slick already loaded');
      return;
    }

    // Use intersection observer to load when carousel is near viewport
    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          loadSlickScript();
          observer.disconnect();
        }
      });
    }, {
      rootMargin: '300px'
    });

    slickElements.forEach(function(element) {
      observer.observe(element);
    });
  }

  /**
   * Load slick script
   */
  function loadSlickScript() {
    // Check if jQuery is loaded
    if (!window.jQuery) {
      console.error('jQuery not loaded, cannot load Slick');
      return;
    }

    // Check if already loaded
    if (window.jQuery.fn.slick) return;

    // Load from existing script tag or create new one
    const existingScript = document.querySelector('script[data-slick-loader]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/jquery.slick/1.4.1/slick.min.js';
    script.async = true;
    script.onload = function() {
      console.log('Slick carousel loaded');
      // Trigger custom event for theme to initialize carousels
      document.dispatchEvent(new CustomEvent('slick:loaded'));
    };
    document.head.appendChild(script);
  }

  /**
   * Prefetch third-party resources on link hover
   */
  function prefetchOnHover() {
    const links = document.querySelectorAll('a[href^="http"]');

    links.forEach(function(link) {
      link.addEventListener('mouseenter', function() {
        const url = link.href;

        // Create prefetch link
        const prefetch = document.createElement('link');
        prefetch.rel = 'prefetch';
        prefetch.href = url;
        prefetch.as = 'document';

        document.head.appendChild(prefetch);
      }, { once: true, passive: true });
    });
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(runOptimizations, 100);
      });
    } else {
      setTimeout(runOptimizations, 100);
    }
  }

  /**
   * Run all optimizations
   */
  function runOptimizations() {
    // Only run if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      console.log('IntersectionObserver not supported, skipping lazy loading');
      return;
    }

    // DISABLED: Merchant widget now loaded via optimized viewport-based loader in theme.liquid
    // lazyLoadMerchantWidget();

    lazyLoadChatWidgets();
    optimizeAvadaLoading();
    lazyLoadSlick();

    // Prefetch on hover (defer to reduce initial load impact)
    if (window.requestIdleCallback) {
      window.requestIdleCallback(prefetchOnHover, { timeout: 5000 });
    } else {
      setTimeout(prefetchOnHover, 5000);
    }
  }

  // Initialize
  init();

})();
